import { type ColumnDef, type Row } from '@tanstack/react-table';
import { Rating } from '@/components/rating';
import { ColumnType } from '@/types/column';
import { FileType } from '@/types/fileType';
import { GalleryView } from '@/types';
import { cn } from '@/lib/utils';

interface RatingColumnOptions<T> {
  id?: string;
  accessorKey: keyof T;
  enableSorting?: boolean;
  enableHiding?: boolean;
  minSize?: number;
  maxSize?: number;
  label: string;
  galleryView: GalleryView;
  size?: number;
  cellClassName?: string;
  headerClassName?: string;
  dataCy?: string;
  onChange?: (row: Row<T>, value: number) => void;
  readOnly?: boolean | ((row: Row<T>) => boolean);
  isLoading?: boolean;
}

export const RatingColumn = <T extends Record<string, any>>(opts: RatingColumnOptions<T>): ColumnDef<T> => {
  const {
    id = ColumnType.Rating,
    accessorKey = 'size',
    label = 'Size',
    galleryView,
    size = 140,
    cellClassName,
    headerClassName,
    enableSorting = false,
    enableHiding = true,
    minSize = 70,
    maxSize = 160,
    dataCy,
    onChange,
    readOnly: readOnlyOpt = true,
    isLoading,
  } = opts;

  return {
    id,
    accessorKey,
    enableSorting,
    enableHiding,
    minSize,
    maxSize,
    ...((cellClassName ?? headerClassName) && {
      meta: {
        ...(cellClassName && { cellClassName: cellClassName }),
        ...(headerClassName && { headerClassName: headerClassName }),
      },
    }),
    size,
    header: () => label,
    cell: ({ row }) => {
      // Don't render rating component for folders or albums (trash page)
      if (row.original.type === FileType.Folder || row.original.type === FileType.Album) {
        return null;
      }

      const isReadOnly = typeof readOnlyOpt === 'function' ? readOnlyOpt(row) : readOnlyOpt;

      return (
        <Rating
          className={cn({
            'items-center': galleryView === GalleryView.TILE,
          })}
          readOnly={isReadOnly}
          isLoading={!row.getIsSelected() || isLoading}
          rating={row.original.rating}
          dataCy={dataCy ?? `${galleryView ? galleryView : 'list'}-${row.index}-${id}`}
          onChange={(newRating) => onChange?.(row, newRating)}
        />
      );
    },
  };
};
